import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  Tooltip,
  TableSortLabel,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { User, RoleEnum } from '../../../types';
import { canPerformAction, ActionKey } from '@/auth/actionMatrix';

interface UsersTableProps {
  users: User[];
  currentUser: User | null;
  onEdit: (user: User) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, newState: boolean) => void;
  onToggleSuperuser: (id: string, newState: boolean) => void;
  onReset2fa: (user: User) => void;
  orderBy: keyof User;
  order: 'asc' | 'desc';
  onSort: (property: keyof User) => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  currentUser,
  onEdit,
  onDelete,
  onToggleActive,
  onToggleSuperuser,
  onReset2fa,
  orderBy,
  order,
  onSort,
}) => {
  const canToggleActive = canPerformAction(currentUser, ActionKey.USERS_TOGGLE_ACTIVE);
  const canToggleSuperuser = canPerformAction(currentUser, ActionKey.USERS_TOGGLE_SUPERUSER);
  const canUpdate = canPerformAction(currentUser, ActionKey.USERS_UPDATE);
  const canDelete = canPerformAction(currentUser, ActionKey.USERS_DELETE);
  const canReset2fa = Boolean(currentUser?.is_superuser || currentUser?.role === RoleEnum.ADMIN);
  const canEditUser = (user: User) => canUpdate && user.role !== RoleEnum.STAFF;

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'username'}
                direction={orderBy === 'username' ? order : 'asc'}
                onClick={() => onSort('username')}
              >
                Логин
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'email'}
                direction={orderBy === 'email' ? order : 'asc'}
                onClick={() => onSort('email')}
              >
                Email
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'role'}
                direction={orderBy === 'role' ? order : 'asc'}
                onClick={() => onSort('role')}
              >
                Роль
              </TableSortLabel>
            </TableCell>
            <TableCell>Активен</TableCell>
            <TableCell>Администратор</TableCell>
            <TableCell>Действия</TableCell>
            <TableCell>2FA</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>{user.username}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                {user.role === RoleEnum.ADMIN ? 'Администратор' :
                 user.role === RoleEnum.CLIENT_MANAGER ? 'Клиент-менеджер' :
                 user.role === RoleEnum.PRODUCT_MANAGER ? 'Продукт-менеджер' :
                 user.role === RoleEnum.SUPPORT ? 'Саппорт' :
                 user.role === RoleEnum.STAFF ? 'Сотрудник' :
                 user.role === RoleEnum.TRADER ? 'Трейдер' :
                 user.role === RoleEnum.TRADERS_OWNER ? 'Владелец трейдеров' :
                 user.role === RoleEnum.MERCHANT ? 'Мерчант' :
                 user.role === RoleEnum.GUEST ? 'Гость' : user.role}
              </TableCell>
              <TableCell>
                <Tooltip title={canToggleActive ? 'Изменить статус' : 'Недостаточно прав'}>
                  <span>
                    <Switch
                      checked={user.is_active}
                      onChange={() => onToggleActive(user.id, !user.is_active)}
                      color="primary"
                      disabled={!canToggleActive}
                    />
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={canToggleSuperuser ? 'Изменить права' : 'Недостаточно прав'}>
                  <span>
                    <Switch
                      checked={user.is_superuser}
                      onChange={() => onToggleSuperuser(user.id, !user.is_superuser)}
                      color="primary"
                      disabled={!canToggleSuperuser}
                    />
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={canEditUser(user) ? 'Редактировать' : 'STAFF изменяется только через контролируемую SQL-операцию'}>
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() => onEdit(user)}
                      disabled={!canEditUser(user)}
                    >
                      <EditIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={canDelete ? 'Удалить' : 'Недостаточно прав'}>
                  <span>
                    <IconButton
                      color="error"
                      onClick={() => onDelete(user.id)}
                      disabled={!canDelete}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={user.totp_enabled ? 'Подключена' : 'Не подключена'}
                  color={user.totp_enabled ? 'success' : 'default'}
                  sx={{ mr: 1 }}
                />
                {user.totp_enabled && (
                  <Tooltip title={canReset2fa ? 'Отключить 2FA' : 'Только для администратора'}>
                    <span>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => onReset2fa(user)}
                        disabled={!canReset2fa}
                      >
                        <SecurityIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UsersTable;
